import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Hand, KeyRound, Hammer, Puzzle } from "lucide-react";

export default function DoorLockModal({ open, onOpenChange, gameId, door, token, character }) {
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(null); // "knock" | "pick-lock" | "break-down" | null
  const [result, setResult] = useState(null);
  const [riddleGuess, setRiddleGuess] = useState("");
  const [puzzleResult, setPuzzleResult] = useState(null);

  useEffect(() => {
    if (open && character?.id) {
      api.get(`/characters/${character.id}/stats`).then((r) => setStats(r.data)).catch(() => setStats(null));
    } else {
      setStats(null);
    }
    setResult(null);
    setPuzzleResult(null);
    setRiddleGuess("");
  }, [open, character?.id, door?.id]);

  if (!door) return null;

  const puzzleLock = door.puzzle_lock;
  const puzzleUnsolved = puzzleLock && !puzzleLock.solved;

  const attemptPuzzle = async () => {
    setBusy("puzzle");
    try {
      const { data } = await api.post(`/games/${gameId}/doors/${door.id}/attempt-puzzle`, { token_id: token.id, riddle_guess: riddleGuess });
      setPuzzleResult(data.solved);
      if (data.solved) toast.success("Puzzle solved!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not attempt the puzzle");
    } finally {
      setBusy(null);
    }
  };

  const knock = async () => {
    setBusy("knock");
    try {
      await api.post(`/games/${gameId}/doors/${door.id}/knock`, { token_id: token.id });
      toast.info("You knock on the door...");
      onOpenChange(false);
    } catch {
      toast.error("Could not knock");
    } finally {
      setBusy(null);
    }
  };

  const roll = async (skillKey, dc, endpoint) => {
    setBusy(endpoint);
    try {
      const mod = stats?.skills?.[skillKey]?.modifier;
      const modifier = typeof mod === "number" ? mod : 0;
      const { data } = await api.post(`/characters/${character.id}/roll`, { game_id: gameId, label: `${door.name || "Door"} (${skillKey})`, modifier });
      const total = data.roll_breakdown.total;
      const passed = total >= dc;
      const { data: doorData } = await api.post(`/games/${gameId}/doors/${door.id}/${endpoint}`, { token_id: token.id, passed });
      setResult({ total, pass: passed, effect: doorData.effect_result, lockpicksRemaining: doorData.lockpicks_remaining });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Roll failed");
    } finally {
      setBusy(null);
    }
  };

  const close = () => {
    onOpenChange(false);
    setResult(null);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="bg-[#322216] border-white/10 text-[#f1e0c5] max-w-sm" data-testid="door-lock-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{door.name || "Locked Door"}</DialogTitle>
          <DialogDescription>This door is locked.</DialogDescription>
        </DialogHeader>
        {result ? (
          <div className="space-y-3">
            <p className={`text-2xl font-display ${result.pass ? "text-[#4d7c0f]" : "text-[#D90429]"}`} data-testid="door-lock-roll-result">
              Rolled {result.total} — {result.pass ? "Success!" : "Failed!"}
            </p>
            {result.lockpicksRemaining != null && !result.pass && (
              <p className="text-xs text-[#D4AF37]" data-testid="door-lockpicks-remaining">Lockpick broke — {result.lockpicksRemaining} left.</p>
            )}
            {result.effect?.message && (
              <p className="text-sm italic text-[#f1e0c5]/80 bg-black/30 border border-white/10 rounded-lg p-2">{result.effect.message}</p>
            )}
            {result.effect?.damage && (
              <p className="text-sm text-[#D90429]">
                Took {result.effect.damage.final} {result.effect.damage.type || ""} damage
                {result.effect.damage.hp_after != null ? ` — HP now ${result.effect.damage.hp_after}` : ""}
              </p>
            )}
            <Button data-testid="door-lock-close-button" onClick={close} className="w-full rounded-full bg-[#D90429] hover:bg-[#b8031f]">Close</Button>
          </div>
        ) : puzzleResult === true ? (
          <div className="space-y-3">
            <p className="text-2xl font-display text-[#4d7c0f]" data-testid="door-puzzle-solved-result">Puzzle solved — the door opens!</p>
            <Button data-testid="door-puzzle-solved-close-button" onClick={close} className="w-full rounded-full bg-[#4d7c0f] hover:bg-[#3d6208]">Close</Button>
          </div>
        ) : (
          <div className="space-y-2">
            {!character?.id && <p className="text-xs text-[#f1e0c5]/50">Create a character first.</p>}
            {puzzleUnsolved && (
              <div className="space-y-2 bg-black/20 border border-[#D4AF37]/30 rounded-lg p-3" data-testid="door-puzzle-section">
                <p className="text-xs text-[#D4AF37] flex items-center gap-1.5"><Puzzle className="w-3.5 h-3.5" /> This door has a puzzle lock.</p>
                {puzzleLock.type === "riddle" ? (
                  <>
                    <p className="text-sm italic">{puzzleLock.riddle_question}</p>
                    <Input data-testid="door-puzzle-riddle-input" value={riddleGuess} onChange={(e) => setRiddleGuess(e.target.value)} placeholder="Your answer…" />
                  </>
                ) : (
                  <p className="text-xs text-[#f1e0c5]/50">Requires: {(puzzleLock.required_item_names || []).join(", ")} in your inventory.</p>
                )}
                {puzzleResult === false && <p className="text-xs text-[#D90429]" data-testid="door-puzzle-failed">Not quite — try again.</p>}
                <Button data-testid="door-puzzle-attempt-button" onClick={attemptPuzzle} disabled={!!busy || !character?.id} className="w-full rounded-full bg-[#D4AF37] text-black hover:bg-[#c19d2e]">Attempt Puzzle</Button>
              </div>
            )}
            <Button data-testid="door-knock-button" onClick={knock} disabled={!!busy} variant="secondary" className="w-full rounded-full gap-2">
              <Hand className="w-4 h-4" /> Knock
            </Button>
            {door.locked && (
              <>
                <Button
                  data-testid="door-pick-button"
                  onClick={() => roll("thievery", door.pick_dc ?? 15, "pick-lock")}
                  disabled={!!busy || !character?.id || (character?.lockpicks || 0) <= 0}
                  className="w-full rounded-full gap-2 bg-[#D4AF37] text-black hover:bg-[#c19d2e]"
                >
                  <KeyRound className="w-4 h-4" /> Pick the Lock (DC {door.pick_dc ?? 15})
                </Button>
                {!(character?.lockpicks > 0) && <p className="text-[10px] text-[#f1e0c5]/40 -mt-1">You have no lockpicks — add some in your Inventory tab.</p>}
                {door.break_dc != null && (
                  <Button
                    data-testid="door-break-button"
                    onClick={() => roll("athletics", door.break_dc, "break-down")}
                    disabled={!!busy || !character?.id}
                    className="w-full rounded-full gap-2 bg-[#7c2d12] hover:bg-[#5c2109]"
                  >
                    <Hammer className="w-4 h-4" /> Break It Down (DC {door.break_dc})
                  </Button>
                )}
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
